<template>
  <funding-base
    :state="state"
    :current-funding-pot-id="state.funding_pot.id"
  >
    <funding-projects-filter
      :route-name="state.route"
      :competitions="state.competitions"
      :funding-pot="state.funding_pot"
      @filter:selected="initialCompetition = $event"
    />
    <template v-if="state.projects.length">
      <div class="container mt-6">
        <div class="flex flex-wrap items-baseline mb-6">
          <div class="w-full md:w-3/4 flex items-baseline">
            <h2 class="text-2xl mb-2 pr-6 mr-6 border-r">
              {{ title }}
            </h2>
            <p class="pb-1">
              {{ filteredProjects.length }} Projects
            </p>
            <template v-if="canSync">
              <span class="w-px h-10 my-auto mx-6 block bg-grey-200"></span>
              <button
                class="text-sm border py-1 px-3 rounded bg-primary-50 border-primary-500 text-primary-500 hover:text-primary-700 transition-colors"
                :disabled="syncing"
                @click="syncAwardedProjects"
              >
                {{ syncing ? "Syncing..." : "Sync Awarded Projects" }}
              </button>
            </template>
            <funding-projects-monitors
              :monitors="monitors"
              :projects="projects"
              :disabled="projects.length === 0"
              class="ml-6"
            />
          </div>
          <div
            class="hidden md:flex w-1/4 border items-center focus-within:bg-white focus-within:border-primary-300 transition-all transition-normal"
          >
            <input
              v-model="search"
              placeholder="Search Projects"
              type="search"
              class="flex-1 px-4 py-2 bg-transparent outline-none"
            />
            <icon
              class="icons-primary w-6 h-6 mr-2"
              icon="search"
            />
          </div>
        </div>
        <template v-if="filteredProjects.length">
          <ul class="list-reset flex flex-wrap">
            <li
              v-for="project in filteredProjects"
              :key="project.uuid"
              class="w-full mb-24"
            >
              <funding-project-item
                :has-contract-template="hasContractTemplate(project.competition_id)"
                :competition-uuid="competitionUuid(project.competition_id)"
                :project-data="project"
                :funding-pot="state.funding_pot.id"
                :collaboration-agreement="requiresCollaborationAgreement(project.competition_id)"
              />
            </li>
          </ul>
        </template>
        <template v-else>
          <div class="w-full flex h-48 items-center justify-center">
            <div class="text-center">
              <h2 class="text-2xl">
                No Results
              </h2>
              <p>
                We can't find any projects for
                <em class="text-primary-500">"{{ search }}"</em>
              </p>
            </div>
          </div>
        </template>
      </div>
    </template>
    <template v-else>
      <div class="container mt-6">
        <div class="w-full flex h-48 items-center justify-center">
          <div class="text-center">
            <h2 class="text-2xl">
              No Projects
            </h2>
            <p>
              There are no projects in this programme yet.
            </p>
            <p
              v-if="canSync"
              class="mt-6"
            >
              <app-button @click="syncAwardedProjects">
                <template v-if="syncing">
                  Syncing...
                </template>
                <template v-else>
                  Sync Awarded Projects
                </template>
              </app-button>
            </p>
          </div>
        </div>
      </div>
    </template>
  </funding-base>
</template>

<script>
import Fuse from "fuse.js";
export default {
  name: "FundingProjectsIndex",
  data() {
    return {
      projects: [],
      syncing: false,
      initialCompetition: null,
      search: ""
    };
  },
  computed: {
    title() {
      return this.initialCompetition
        ? this.filteredCompetitions[0].title + " Projects"
        : this.search
        ? 'Search results for: "' + this.search + '"'
        : "All Projects";
    },
    canSync() {
      return this.state.can_sync;
    },
    competitions() {
      return !this.initialCompetition
        ? this.state.competitions
        : this.filteredCompetitions;
    },
    filteredCompetitions() {
      return this.state.competitions.filter(
        competition => competition.uuid === this.initialCompetition
      );
    },
    filteredProjects() {
      if (!this.search) return this.projects;
      const options = {
        shouldSort: true,
        tokenize: true,
        threshold: 0.2,
        location: 0,
        distance: 100,
        maxPatternLength: 32,
        minMatchCharLength: 3,
        keys: ["ref", "title", "partner_entries.name"]
      };
      const results = new Fuse(this.projects, options);
      return results.search(this.search);
    },
    monitors() {
      return this.state.monitors;
    }
  },
  created() {
    this.projects = this.state.projects;
  },
  methods: {
    syncAwardedProjects() {
      if (!this.canSync) return;
      this.syncing = true;
      axios
        // make request
        .post(route("funding.projects.sync", [this.state.funding_pot.id]))
        .then(({ data }) => (this.projects = Object.assign(this.projects, data.projects)))
        // eslint-disable-next-line no-console
        .catch(error => console.error(error))
        .finally(() => (this.syncing = false));
    },
    projectOrder(a, b) {
      if (!a.terminated_at && !b.terminated_at) {
        return -1;
      }
      return 1;
    },
    requiresCollaborationAgreement(competition_id) {
      const projectCompetition = this.competitions.find(
        competition => competition.id === competition_id
      );
      if (!projectCompetition) return false;
      return projectCompetition.requires_collaboration_agreement_upload
        ? true
        : false;
    },
    competitionUuid(competition_id) {
      const projectCompetition = this.competitions.find(
        competition => competition.id === competition_id
      );
      if (!projectCompetition) return false;
      return projectCompetition.uuid;
    },
    hasContractTemplate(competition_id) {
      const projectCompetition = this.competitions.find(competition => competition.id === competition_id);
      if (!projectCompetition) return false;
      return projectCompetition.contract_competition_template_id ? true : false;
    }
  },
};
</script>
